import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  BellRing,
  Bot,
  CheckCheck,
  ChevronDown,
  Clock,
  Filter,
  Hand,
  MessageSquareOff,
  PhoneForwarded,
  PhoneOff,
  Undo2,
  UserRoundCheck,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { formatTime } from '@/lib/conversationFormat';
import { deliveryOf } from '@/lib/deliveryStatus';
import { serverNow } from '@/lib/serverClock';
import { DeliveryFailure, DeliveryMark } from './DeliveryMark';
import WhatsAppText from './WhatsAppText';
import type { ConversationChannel, TranscriptMessage } from '@/services/conversationService';

/** A message that arrived while the chat was open fades in once (not with reduced motion). */
export const FRESH_ENTER = 'animate-in fade-in-0 slide-in-from-bottom-1 duration-300 motion-reduce:animate-none';

type Props = {
  message: TranscriptMessage;
  /** Nur WhatsApp-Nachrichten tragen die `*fett*`-Auszeichnung; Telefon-Transkripte sind Rohtext. */
  channel: ConversationChannel;
  /** Logged-in account: its own staff messages are labelled "Du". */
  me?: string | null;
  /** First bubble of a run by the same sender (shows the sender label). */
  groupStart?: boolean;
  /** Last bubble of the run (carries the tail). */
  groupEnd?: boolean;
  /** Arrived live while the chat was open: fades in once. */
  fresh?: boolean;
  /**
   * Server-clock ms for the delivery mark: "on its way" expires after a few
   * minutes. The timeline hands down its one ticking clock; without it the
   * clock is read at render time.
   */
  now?: number;
};

const EVENT_ICONS: Record<string, typeof Wrench> = {
  asr_filtered: Filter,
  tool: Wrench,
  transfer: PhoneForwarded,
  end: PhoneOff,
  takeover_started: UserRoundCheck,
  takeover_released: Bot,
  bot_suppressed: MessageSquareOff,
  requested: Hand,
  handoff_requested: Hand,
  handoff_callback: BellRing,
  handoff_all_busy: Users,
  handoff_declined: X,
  callback: BellRing,
  callback_requested: BellRing,
  callback_resolved: CheckCheck,
  all_busy: Users,
  agent_resumed: Undo2,
  idle: Bot,
  expired: Bot,
  released: Bot,
  window_expired: Clock,
};

/**
 * Fixed German wording for handoff and live-chat event rows (livecenter §3
 * reasons); `content` adds the detail (reason, author, held text).
 */
export const EVENT_LABELS: Record<string, string> = {
  takeover_started: 'Gespräch übernommen',
  takeover_released: 'Zurück an den Agenten',
  bot_suppressed: 'Agentenantwort zurückgehalten',
  requested: 'Kunde möchte einen Mitarbeiter',
  handoff_requested: 'Kunde möchte einen Mitarbeiter',
  // Event lines the handoff writes (staff_handoff_service EVENT_KIND_*).
  handoff_callback: 'Rückmeldung gewünscht',
  handoff_all_busy: 'Kunde wollte einen Mitarbeiter – niemand online',
  handoff_declined: 'Kunde möchte keine Rückmeldung',
  callback: 'Rückmeldung gewünscht',
  callback_requested: 'Rückmeldung gewünscht',
  callback_resolved: 'Rückmeldung erledigt',
  all_busy: 'Alle beschäftigt – Rückmeldung angeboten',
  agent_resumed: 'Agent ist wieder eingesprungen',
  idle: 'Zurück an den Agenten – Ruhe im Chat',
  expired: 'Zurück an den Agenten – Übernahme abgelaufen',
  released: 'Zurück an den Agenten',
  window_expired: 'Antwortfenster abgelaufen',
};

/** Events that need the salon's attention get the brand accent. */
const ACCENT_EVENTS = new Set([
  'requested',
  'handoff_requested',
  'handoff_callback',
  'handoff_all_busy',
  'callback',
  'callback_requested',
  'all_busy',
  'agent_resumed',
]);

/** The backend stores self-explanatory sentences ("Gespräch übernommen von X",
 *  "Zurück an den Agenten (Übernahme abgelaufen)", …). The UI shows its own
 *  label, so the label part is stripped and only the detail remains. */
const EVENT_PREFIXES: Record<string, RegExp> = {
  takeover_started: /^Gespräch übernommen(?:\s+von)?\s*/i,
  takeover_released: /^Zurück an den Agenten\s*/i,
  bot_suppressed: /^Agentenantwort zurückgehalten:?\s*/i,
};

export function liveEventDetail(kind: string, content?: string | null): string {
  const raw = (content ?? '').trim();
  const prefix = EVENT_PREFIXES[kind];
  let stripped = prefix ? raw.replace(prefix, '') : raw;
  const label = EVENT_LABELS[kind];
  if (!prefix && label && stripped.toLowerCase().startsWith(label.toLowerCase())) {
    stripped = stripped.slice(label.length).replace(/^[\s:·–-]+/, '');
  }
  return stripped.replace(/^\((.*)\)$/s, '$1').trim();
}

function EventShell({
  message,
  accent = false,
  fresh = false,
  children,
}: {
  message: TranscriptMessage;
  accent?: boolean;
  fresh?: boolean;
  children: React.ReactNode;
}) {
  const Icon = EVENT_ICONS[message.kind ?? ''] ?? Wrench;
  const time = formatTime(message.ts);
  return (
    <div className={cn('flex w-full justify-center py-1', fresh && FRESH_ENTER)}>
      <div
        className={cn(
          'flex max-w-[92%] items-start gap-1.5 rounded-2xl border px-3 py-1 text-[11.5px] leading-snug text-muted-foreground',
          accent
            ? 'border-brand/35 bg-card text-foreground shadow-sm'
            : 'border-dashed border-border bg-card/70',
        )}
      >
        <Icon className={cn('mt-[2px] h-3 w-3 flex-shrink-0', accent && 'text-brand')} aria-hidden="true" />
        <div className="min-w-0 break-words">{children}</div>
        {time && <span className="flex-shrink-0 tabular-nums text-foreground/60">{time}</span>}
      </div>
    </div>
  );
}

/** The bot reply that was held back because staff took over; text on demand. */
function SuppressedLine({ message, fresh }: { message: TranscriptMessage; fresh?: boolean }) {
  const [open, setOpen] = useState(false);
  const held = liveEventDetail('bot_suppressed', message.content);
  return (
    <EventShell message={message} fresh={fresh}>
      <span className="font-medium text-foreground/80">{EVENT_LABELS.bot_suppressed}</span>
      {held && (
        <>
          {' '}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="inline-flex items-center gap-0.5 rounded px-1 text-[11px] font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {open ? 'Text ausblenden' : 'Text anzeigen'}
            <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} aria-hidden="true" />
          </button>
          {open && (
            <p className="mt-1 whitespace-pre-wrap border-l-2 border-border pl-2 italic text-muted-foreground">{held}</p>
          )}
        </>
      )}
    </EventShell>
  );
}

/**
 * A system event: filtered speech, a tool the agent ran, a transfer, the end
 * reason, or a handoff change. A centered chip, so it reads as "what
 * happened" and never as something anyone said.
 */
function EventLine({ message, fresh }: { message: TranscriptMessage; fresh?: boolean }) {
  const kind = message.kind ?? '';
  if (kind === 'bot_suppressed') return <SuppressedLine message={message} fresh={fresh} />;

  const label = EVENT_LABELS[kind];
  if (!label) {
    return (
      <EventShell message={message} fresh={fresh}>
        <span>{message.content}</span>
      </EventShell>
    );
  }

  const contentDetail = liveEventDetail(kind, message.content);
  const detail = (kind === 'takeover_started' ? message.author || contentDetail : contentDetail)?.trim();
  return (
    <EventShell message={message} accent={ACCENT_EVENTS.has(kind)} fresh={fresh}>
      <span className="font-medium text-foreground">{label}</span>
      {detail && detail !== label && (
        <span>{kind === 'takeover_started' ? ` (${detail})` : ` · ${detail}`}</span>
      )}
    </EventShell>
  );
}

function isSameAccount(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * One chat bubble. Seiten wie im Salon gedacht (Toms Entscheidung 30.09.):
 * der Kunde links, der Salon rechts — die KI hell mit „KI“, Mitarbeiter
 * kräftig im Markenblau mit „Du“ bzw. dem Namen.
 */
export function MessageBubble({
  message,
  channel,
  me = null,
  groupStart = true,
  groupEnd = true,
  fresh = false,
  now,
}: Props) {
  if (message.role === 'event') return <EventLine message={message} fresh={fresh} />;

  const isCustomer = message.role === 'user';
  const isStaff = message.role === 'staff';
  const isAgent = !isCustomer && !isStaff;
  const time = formatTime(message.ts);
  const staffLabel = isSameAccount(message.author, me) ? 'Du' : message.author?.trim() || 'Salon';
  const senderLabel = isAgent ? 'KI' : isStaff ? staffLabel : null;
  const buttons = isAgent ? (message.buttons ?? []).filter((b) => b.trim()) : [];
  // Only what we sent over WhatsApp has a delivery (customer rows, phone transcripts: none).
  const delivery = channel === 'whatsapp' && !isCustomer ? deliveryOf(message, now ?? serverNow()) : null;

  return (
    <div
      className={cn(
        'flex w-full',
        isCustomer ? 'justify-start pr-10 sm:pr-16' : 'justify-end pl-10 sm:pl-16',
        groupStart ? 'mt-2.5' : 'mt-0.5',
        fresh && FRESH_ENTER,
      )}
      data-side={isCustomer ? 'customer' : 'salon'}
    >
      <div className={cn('flex max-w-[34rem] flex-col gap-1', isCustomer ? 'items-start' : 'items-end')}>
        <div
          className={cn(
            'max-w-full rounded-2xl px-3.5 pb-1.5 pt-2 text-[14px] leading-relaxed',
            isCustomer && 'border border-border/80 bg-card text-card-foreground shadow-sm',
            isAgent && 'bg-brand-soft text-foreground ring-1 ring-inset ring-brand/15',
            isStaff && 'bg-brand text-brand-foreground shadow-sm',
            groupEnd && (isCustomer ? 'rounded-bl-md' : 'rounded-br-md'),
          )}
        >
          {senderLabel && groupStart && (
            <span
              className={cn(
                'mb-0.5 block text-[10.5px] font-bold uppercase tracking-[0.08em]',
                // brand-strong: the plain brand blue misses 4.5:1 on the light KI bubble.
                isAgent ? 'text-brand-strong' : 'normal-case tracking-normal text-brand-foreground/90',
              )}
            >
              {senderLabel}
            </span>
          )}
          <p className="whitespace-pre-wrap break-words">
            {channel === 'whatsapp' ? <WhatsAppText text={message.content} /> : message.content}
          </p>
          {(time || delivery) && (
            <span
              className={cn(
                'mt-0.5 flex items-center justify-end gap-1 text-[11px] tabular-nums',
                isStaff ? 'text-brand-foreground/90' : 'text-foreground/65',
              )}
            >
              {time && <span>{time}</span>}
              {delivery && <DeliveryMark delivery={delivery} surface={isStaff ? 'brand' : 'soft'} />}
            </span>
          )}
        </div>
        {delivery && <DeliveryFailure delivery={delivery} />}
        {buttons.length > 0 && (
          // What the customer got with this message (WhatsApp reply buttons),
          // below the bubble like WhatsApp shows them — not pressable here.
          <div role="group" aria-label="Antwortknöpfe für den Kunden" className="flex w-full flex-wrap justify-end gap-1">
            {buttons.map((label) => (
              <span
                key={label}
                className="min-w-[7rem] flex-1 rounded-xl border border-brand/20 bg-card px-3 py-1 text-center text-[13px] font-medium text-brand-strong shadow-sm"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MessageBubble;
